// File: /opt/dev_environment/assets/workspace/app-init/src/components/SwipeableCard/SwipeableCard.js

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { LEITNER_COMPARTMENTS, WORD_CATEGORIES } from '../../constants/leitner';
import { fetchPronunciation, fetchVerbPronunciation } from '../../services/pronunciationApi';
import './SwipeableCard.css';

const SWIPE_THRESHOLD = 80;

const SpeakerIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
  </svg>
);

function SwipeableCard({
  card,
  cardStats,
  direction,
  onSwipeRight,
  onSwipeLeft,
  disabled = false,
}) {
  const cardRef = useRef(null);
  const dragRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    deltaX: 0,
  });

  const [deltaX, setDeltaX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [swipeResult, setSwipeResult] = useState(null);
  const [isExiting, setIsExiting] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);

  // Pronunciation state
  const [audioState, setAudioState] = useState('idle'); // idle | loading | playing | error
  const [exampleSentence, setExampleSentence] = useState('');
  const [pronunciationError, setPronunciationError] = useState('');
  const audioRef = useRef(null);
  const audioUrlRef = useRef(null);

  const isExitingRef = useRef(false);
  const disabledRef = useRef(disabled);

  useEffect(() => {
    isExitingRef.current = isExiting;
  }, [isExiting]);

  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  const isVerbCard = card?.type === 'verb';

  // Determine display word based on direction and card type
  const getDisplayWord = useCallback(() => {
    if (!card) return '';
    if (isVerbCard) {
      if (direction === 'IT→DE') {
        return card.conjugatedAnswer;
      }
      if (direction === 'Gemischt') {
        const hash = card.id.charCodeAt(0) + card.id.charCodeAt(card.id.length - 1);
        return hash % 2 === 0
          ? (card.germanConjugation || card.germanInfinitive)
          : card.conjugatedAnswer;
      }
      return card.germanConjugation || card.germanInfinitive;
    }
    if (direction === 'IT→DE') return card.italianWord;
    if (direction === 'Gemischt') {
      const hash = card.id.charCodeAt(0) + card.id.charCodeAt(card.id.length - 1);
      return hash % 2 === 0 ? card.germanWord : card.italianWord;
    }
    return card.germanWord;
  }, [card, direction, isVerbCard]);

  const getDisplaySubtitle = useCallback(() => {
    if (!card) return '';
    if (isVerbCard) {
      if (direction === 'IT→DE') {
        return `${card.baseVerb} — ${card.targetFormDescription}`;
      }
      if (direction === 'Gemischt') {
        const hash = card.id.charCodeAt(0) + card.id.charCodeAt(card.id.length - 1);
        return hash % 2 === 0
          ? `${card.germanInfinitive} — ${card.targetFormDescriptionGerman || card.targetFormDescription}`
          : `${card.baseVerb} — ${card.targetFormDescription}`;
      }
      return `${card.germanInfinitive} — ${card.targetFormDescriptionGerman || card.targetFormDescription}`;
    }
    return '';
  }, [card, direction, isVerbCard]);

  const getAnswerWord = useCallback(() => {
    if (!card) return '';
    if (isVerbCard) {
      if (direction === 'IT→DE') {
        return card.germanConjugation || card.germanInfinitive;
      }
      if (direction === 'Gemischt') {
        const hash = card.id.charCodeAt(0) + card.id.charCodeAt(card.id.length - 1);
        return hash % 2 === 0
          ? card.conjugatedAnswer
          : (card.germanConjugation || card.germanInfinitive);
      }
      return card.conjugatedAnswer;
    }
    if (direction === 'IT→DE') return card.germanWord;
    if (direction === 'Gemischt') {
      const hash = card.id.charCodeAt(0) + card.id.charCodeAt(card.id.length - 1);
      return hash % 2 === 0 ? card.italianWord : card.germanWord;
    }
    return card.italianWord;
  }, [card, direction, isVerbCard]);

  const getAnswerSubtitle = useCallback(() => {
    if (!card || !isVerbCard) return '';
    if (direction === 'IT→DE') {
      return `${card.germanInfinitive} — ${card.targetFormDescriptionGerman || ''}`;
    }
    if (direction === 'Gemischt') {
      const hash = card.id.charCodeAt(0) + card.id.charCodeAt(card.id.length - 1);
      return hash % 2 === 0
        ? `${card.baseVerb} — ${card.targetFormDescription}`
        : `${card.germanInfinitive} — ${card.targetFormDescriptionGerman || ''}`;
    }
    return `${card.baseVerb} — ${card.targetFormDescription}`;
  }, [card, direction, isVerbCard]);

  const getAnswerLanguageLabel = useCallback(() => {
    if (!card) return '';
    if (isVerbCard) {
      if (direction === 'IT→DE') return 'Deutsch';
      if (direction === 'Gemischt') {
        const hash = card.id.charCodeAt(0) + card.id.charCodeAt(card.id.length - 1);
        return hash % 2 === 0 ? 'Italienisch' : 'Deutsch';
      }
      return 'Italienisch';
    }
    if (direction === 'IT→DE') return 'Deutsch';
    if (direction === 'Gemischt') {
      const hash = card.id.charCodeAt(0) + card.id.charCodeAt(card.id.length - 1);
      return hash % 2 === 0 ? 'Italienisch' : 'Deutsch';
    }
    return 'Italienisch';
  }, [card, direction, isVerbCard]);

  const getCategoryLabel = (value) => {
    const cat = WORD_CATEGORIES.find((c) => c.value === value);
    return cat ? cat.label : value;
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
    };
  }, []);

  // Reset state when card changes
  useEffect(() => {
    setSwipeResult(null);
    setIsExiting(false);
    setDeltaX(0);
    setIsDragging(false);
    setIsFlipped(false);
    setAudioState('idle');
    setExampleSentence('');
    setPronunciationError('');
    dragRef.current = { isDragging: false, startX: 0, startY: 0, deltaX: 0 };
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  }, [card?.id]);

  // --- Pronunciation handler ---
  const handlePlayPronunciation = useCallback(async (e) => {
    e.stopPropagation();
    if (!card) return;

    // If already playing, stop it
    if (audioState === 'playing' && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setAudioState('idle');
      return;
    }

    // If loading, ignore
    if (audioState === 'loading') return;

    setAudioState('loading');
    setPronunciationError('');

    try {
      let result;
      if (isVerbCard) {
        result = await fetchVerbPronunciation(card);
      } else {
        result = await fetchPronunciation(card.germanWord, card.italianWord);
      }

      // Clean up previous audio URL
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
      audioUrlRef.current = result.audioUrl;

      // Set example sentence
      if (result.exampleSentence) {
        setExampleSentence(decodeURIComponent(result.exampleSentence));
      }

      // Play audio
      const audio = new Audio(result.audioUrl);
      audioRef.current = audio;

      audio.addEventListener('ended', () => {
        setAudioState('idle');
      });

      audio.addEventListener('error', () => {
        setAudioState('error');
        setPronunciationError('Audio konnte nicht abgespielt werden');
      });

      setAudioState('playing');
      await audio.play();
    } catch (err) {
      console.error('Pronunciation fetch error:', err);
      setAudioState('error');
      setPronunciationError('Aussprache konnte nicht geladen werden');
    }
  }, [card, audioState, isVerbCard]);

  const handleTranslateClick = useCallback((e) => {
    e.stopPropagation();
    if (disabledRef.current || isExitingRef.current) return;
    setIsFlipped((prev) => !prev);
  }, []);

  const handleFlippedCardClick = useCallback((e) => {
    if (isFlipped && !isExitingRef.current) {
      e.stopPropagation();
      setIsFlipped(false);
    }
  }, [isFlipped]);

  const handleRightButtonClick = useCallback((e) => {
    e.stopPropagation();
    if (disabledRef.current || isExitingRef.current) return;
    setSwipeResult('right');
    setIsExiting(true);
    setDeltaX(100);
    setIsFlipped(false);
    if (audioRef.current) { audioRef.current.pause(); }
    setTimeout(() => {
      if (onSwipeRight) onSwipeRight(card);
    }, 300);
  }, [card, onSwipeRight]);

  const handleLeftButtonClick = useCallback((e) => {
    e.stopPropagation();
    if (disabledRef.current || isExitingRef.current) return;
    setSwipeResult('left');
    setIsExiting(true);
    setDeltaX(-100);
    setIsFlipped(false);
    if (audioRef.current) { audioRef.current.pause(); }
    setTimeout(() => {
      if (onSwipeLeft) onSwipeLeft(card);
    }, 300);
  }, [card, onSwipeLeft]);

  const handlePointerDown = useCallback((clientX, clientY) => {
    if (disabledRef.current || isExitingRef.current) return;
    dragRef.current = {
      isDragging: true,
      startX: clientX,
      startY: clientY,
      deltaX: 0,
    };
    setIsDragging(true);
    setDeltaX(0);
  }, []);

  const handlePointerMove = useCallback((clientX) => {
    if (!dragRef.current.isDragging) return;
    const dx = clientX - dragRef.current.startX;
    dragRef.current.deltaX = dx;
    setDeltaX(dx);
  }, []);

  const handlePointerUp = useCallback(() => {
    if (!dragRef.current.isDragging) return;

    const dx = dragRef.current.deltaX;
    dragRef.current.isDragging = false;
    setIsDragging(false);

    if (dx > SWIPE_THRESHOLD) {
      // Swipe right — known
      setSwipeResult('right');
      setIsExiting(true);
      setDeltaX(dx);
      setIsFlipped(false);
      if (audioRef.current) { audioRef.current.pause(); }
      setTimeout(() => {
        if (onSwipeRight) onSwipeRight(card);
      }, 300);
    } else if (dx < -SWIPE_THRESHOLD) {
      // Swipe left — not known, immediate exit (no reveal)
      setSwipeResult('left');
      setIsExiting(true);
      setDeltaX(dx);
      setIsFlipped(false);
      if (audioRef.current) { audioRef.current.pause(); }
      setTimeout(() => {
        if (onSwipeLeft) onSwipeLeft(card);
      }, 300);
    } else {
      setDeltaX(0);
      setSwipeResult(null);
    }
  }, [card, onSwipeRight, onSwipeLeft]);

  const onTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    handlePointerDown(touch.clientX, touch.clientY);
  }, [handlePointerDown]);

  const onTouchMove = useCallback((e) => {
    if (dragRef.current.isDragging) {
      e.preventDefault();
    }
    const touch = e.touches[0];
    handlePointerMove(touch.clientX);
  }, [handlePointerMove]);

  const onTouchEnd = useCallback(() => {
    handlePointerUp();
  }, [handlePointerUp]);

  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    handlePointerDown(e.clientX, e.clientY);

    const onMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      handlePointerMove(moveEvent.clientX);
    };

    const onMouseUp = () => {
      handlePointerUp();
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [handlePointerDown, handlePointerMove, handlePointerUp]);

  if (!card) return null;

  const borderColor = LEITNER_COMPARTMENTS[card.compartment]?.color || 'var(--color-fach-1)';
  const wordColor = LEITNER_COMPARTMENTS[card.compartment]?.color || 'var(--color-fach-1)';

  let translateX = 0;
  let rotation = 0;

  if (isDragging) {
    translateX = deltaX;
    rotation = deltaX * 0.08;
  } else if (swipeResult === 'right' && isExiting) {
    translateX = 400;
    rotation = 20;
  } else if (swipeResult === 'left' && isExiting) {
    translateX = -400;
    rotation = -20;
  }

  const opacity = isExiting ? 0 : 1;

  const swipeProgress = Math.min(Math.abs(deltaX) / SWIPE_THRESHOLD, 1);
  const isSwipingRight = isDragging && deltaX > 0;
  const isSwipingLeft = isDragging && deltaX < 0;

  const greenOverlayOpacity = isSwipingRight ? swipeProgress * 0.25 : 0;
  const redOverlayOpacity = isSwipingLeft ? swipeProgress * 0.25 : 0;

  const cardStyle = {
    borderColor,
    transform: `translateX(${translateX}px) rotate(${rotation}deg)`,
    opacity,
    transition: isDragging ? 'none' : 'transform 0.3s ease, opacity 0.3s ease',
  };

  const buttonsDisabled = isExiting || disabled;

  const getPlayButtonLabel = () => {
    if (audioState === 'loading') return 'Laden…';
    if (audioState === 'playing') return 'Stopp';
    return 'Aussprache';
  };

  return (
    <div
      ref={cardRef}
      className={`swipeable-card ${isExiting ? 'swipeable-card--exiting' : ''}`}
      style={cardStyle}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      role="article"
      aria-label={isVerbCard ? `Verbkarte: ${card.baseVerb}` : `Vokabelkarte: ${getDisplayWord()}`}
    >
      {/* Swipe direction overlays */}
      <div
        className="swipeable-card__overlay swipeable-card__overlay--right"
        style={{ opacity: greenOverlayOpacity }}
      >
        <span className="swipeable-card__overlay-icon">✓</span>
      </div>
      <div
        className="swipeable-card__overlay swipeable-card__overlay--left"
        style={{ opacity: redOverlayOpacity }}
      >
        <span className="swipeable-card__overlay-icon">✗</span>
      </div>

      {/* Score indicators */}
      {cardStats && (
        <div className="swipeable-card__score">
          <span className="swipeable-card__score-item swipeable-card__score-item--correct">
            ✓{cardStats.correct}
          </span>
          <span className="swipeable-card__score-item swipeable-card__score-item--wrong">
            ✗{cardStats.wrong}
          </span>
          <span className="swipeable-card__score-item swipeable-card__score-item--total">
            Σ{cardStats.total}
          </span>
        </div>
      )}

      {/* Flip container */}
      <div className={`swipeable-card__flip-inner ${isFlipped ? 'swipeable-card__flip-inner--flipped' : ''}`}>
        {/* Front face */}
        <div className="swipeable-card__flip-front">
          <div className="swipeable-card__body">
            <div className="swipeable-card__word" style={{ color: wordColor }}>
              {getDisplayWord()}
            </div>
            {(isVerbCard && getDisplaySubtitle()) && (
              <div style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
                fontWeight: 'var(--font-weight-semibold)',
                marginTop: '2px',
                textAlign: 'center',
              }}>
                {getDisplaySubtitle()}
              </div>
            )}

            <div className="swipeable-card__meta">
              <span
                className="swipeable-card__meta-badge"
                style={{ backgroundColor: LEITNER_COMPARTMENTS[card.compartment]?.color }}
              >
                Fach {card.compartment}
              </span>
              {isVerbCard ? (
                <span>{card.conjugationFormLabel}</span>
              ) : (
                <>
                  {card.gender && <span>{card.gender}</span>}
                  {card.category && <span>{getCategoryLabel(card.category)}</span>}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Back face (translation/conjugation) */}
        <div
          className="swipeable-card__flip-back"
          onClick={handleFlippedCardClick}
          role="button"
          tabIndex={isFlipped ? 0 : -1}
          aria-label="Karte zurückdrehen"
        >
          <span className="swipeable-card__flip-back-label">
            {getAnswerLanguageLabel()}
          </span>
          <div className="swipeable-card__flip-back-word">
            {getAnswerWord()}
          </div>
          {(isVerbCard && getAnswerSubtitle()) && (
            <div style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-secondary)',
              marginTop: 'var(--space-xs)',
              textAlign: 'center',
            }}>
              {getAnswerSubtitle()}
            </div>
          )}
          <div className="swipeable-card__flip-back-meta">
            <span
              className="swipeable-card__flip-back-badge"
              style={{ backgroundColor: LEITNER_COMPARTMENTS[card.compartment]?.color }}
            >
              Fach {card.compartment}
            </span>
            {isVerbCard ? (
              <span>{card.conjugationFormLabel}</span>
            ) : (
              <>
                {card.gender && <span>{card.gender}</span>}
                {card.category && <span>{getCategoryLabel(card.category)}</span>}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Pronunciation Section */}
      {!isExiting && (
        <div className="swipeable-card__pronunciation">
          <div className="swipeable-card__pronunciation-row">
            <button
              className={`swipeable-card__play-btn ${audioState === 'loading' ? 'swipeable-card__play-btn--loading' : ''} ${audioState === 'playing' ? 'swipeable-card__play-btn--playing' : ''}`}
              type="button"
              onClick={handlePlayPronunciation}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              disabled={isExiting}
              aria-label={audioState === 'playing' ? 'Aussprache stoppen' : 'Aussprache anhören'}
            >
              <span className="swipeable-card__play-btn-icon">
                {audioState === 'loading' ? (
                  '⏳'
                ) : audioState === 'playing' ? (
                  '⏹'
                ) : (
                  <SpeakerIcon />
                )}
              </span>
              {getPlayButtonLabel()}
            </button>
          </div>

          {/* Error message */}
          {pronunciationError && (
            <div className="swipeable-card__pronunciation-error">
              {pronunciationError}
            </div>
          )}
        </div>
      )}

      {/* Action buttons inside card (bottom row) */}
      {!isExiting && (
        <div className="swipeable-card__actions">
          <button
            className="swipeable-card__action-btn swipeable-card__action-btn--left"
            type="button"
            onClick={handleLeftButtonClick}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            disabled={buttonsDisabled}
            aria-label="Nicht gewusst"
          >
            ← Nicht gewusst
          </button>

          <button
            className={`swipeable-card__action-btn swipeable-card__action-btn--translate ${isFlipped ? 'swipeable-card__action-btn--translate--active' : ''}`}
            type="button"
            onClick={handleTranslateClick}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            disabled={buttonsDisabled}
            aria-label={isFlipped ? 'Antwort verbergen' : 'Antwort zeigen'}
            aria-pressed={isFlipped}
          >
            🔄 Übersetzen
          </button>

          <button
            className="swipeable-card__action-btn swipeable-card__action-btn--right"
            type="button"
            onClick={handleRightButtonClick}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            disabled={buttonsDisabled}
            aria-label="Gewusst"
          >
            Gewusst →
          </button>
        </div>
      )}
    </div>
  );
}

export default SwipeableCard;